import React, { useEffect, useMemo, useRef, useState } from "react";
import {
  flexRender,
  getCoreRowModel,
  getSortedRowModel,
  type ColumnDef,
  type SortingState,
  useReactTable,
} from "@tanstack/react-table";
import { BsArrowLeftSquare, BsArrowRightSquare } from "react-icons/bs";
import { RiArrowDownSLine } from "react-icons/ri";
import { TailSpin } from "react-loader-spinner";

import { getAssetImage } from "../../constant";
import type { UserTransactionStatusFilter } from "../../lib/api";
import type { BaseRowItem } from "../../components/data-table";
import {
  formatTransactionDate,
  getTransactionDateIso,
  formatLedgerBalancesPlainText,
  getTransactionAssetLabel,
  getTransactionSourceLabel,
  getTransactionSourceKey,
  getTransactionTypeLabel,
  getUserTransactionAmountPlainText,
  resolveUserTransactionStatus,
  sortAmountValue,
  sourceBadgeClass,
  statusBadgeClass,
  USER_ACTIVITY_TX_PAGE_SIZE,
  type UserActivityTransaction,
} from "./userActivityTransactionUtils";

export {
  USER_ACTIVITY_TX_PAGE_SIZE,
  normalizeUserTransactionsResponse,
  getUserTransactionStatusLabel,
  getUserTransactionAmountPlainText,
  resolveUserTransactionStatus,
  utilityData,
  type UserActivityTransaction,
  type UserTransactionsPageMeta,
  type UserTransactionsPageResponse,
} from "./userActivityTransactionUtils";

export type UserActivityTxRow = BaseRowItem & {
  raw: UserActivityTransaction;
  assetLabel: string;
  assetImage?: string;
  typeLabel: string;
  sourceLabel: string;
  sourceKey: string;
  amountText: string;
  transId: string;
  dateLabel: string;
  statusLabel: string;
  statusTone: ReturnType<typeof resolveUserTransactionStatus>["tone"];
  sortAsset: string;
  sortAmount: number;
  sortDate: number;
  balanceBeforeText: string;
  balanceAfterText: string;
};

function buildRows(
  transactions: UserActivityTransaction[]
): UserActivityTxRow[] {
  return transactions.map((tx) => {
    const st = resolveUserTransactionStatus(tx);
    const assetLabel = getTransactionAssetLabel(tx);
    const iso = getTransactionDateIso(tx);
    const ledgerBalances = formatLedgerBalancesPlainText(tx.ledger);
    return {
      id: tx._id,
      raw: tx,
      assetLabel,
      assetImage: getAssetImage(assetLabel),
      typeLabel: getTransactionTypeLabel(tx),
      sourceLabel: getTransactionSourceLabel(tx),
      sourceKey: getTransactionSourceKey(tx),
      amountText: getUserTransactionAmountPlainText(tx),
      transId: tx._id,
      dateLabel: formatTransactionDate(tx),
      statusLabel: st.label,
      statusTone: st.tone,
      sortAsset: assetLabel,
      sortAmount: sortAmountValue(tx),
      sortDate: iso ? new Date(iso).getTime() : 0,
      balanceBeforeText: ledgerBalances.before,
      balanceAfterText: ledgerBalances.after,
    };
  });
}

function shortenId(id: string): string {
  if (!id || id.length <= 12) return id;
  return `${id.slice(0, 8)}…${id.slice(-4)}`;
}

function createColumns(
  onView: (tx: UserActivityTransaction) => void
): ColumnDef<UserActivityTxRow, unknown>[] {
  return [
    {
      id: "date",
      accessorKey: "sortDate",
      header: () => (
        <span className="text-[12px] font-medium text-[#869AA9]">Date</span>
      ),
      cell: ({ row }) => (
        <span className="whitespace-nowrap text-[12px] text-[#C6D0D7] sm:text-[13px]">
          {row.original.dateLabel}
        </span>
      ),
    },
    {
      accessorKey: "typeLabel",
      header: () => (
        <span className="text-[12px] font-medium text-[#869AA9]">Type</span>
      ),
      enableSorting: false,
      cell: ({ row }) => (
        <span className="text-[12px] font-medium text-white uppercase">
          {row.original.typeLabel}
        </span>
      ),
    },
    {
      accessorKey: "sourceLabel",
      header: () => (
        <span className="text-[12px] font-medium text-[#869AA9]">Source</span>
      ),
      enableSorting: false,
      cell: ({ row }) => (
        <span
          className={`inline-flex max-w-[128px] items-center justify-center whitespace-nowrap rounded-full px-2.5 py-1 text-[10px] font-semibold tracking-wide ${sourceBadgeClass(
            row.original.sourceKey
          )}`}
          title={row.original.sourceLabel}
        >
          {row.original.sourceLabel}
        </span>
      ),
    },
    {
      accessorKey: "sortAsset",
      header: () => (
        <span className="text-[12px] font-medium text-[#869AA9]">Asset</span>
      ),
      cell: ({ row }) => (
        <div className="flex min-w-0 items-center gap-2">
          <div className="flex h-6 w-6 shrink-0 items-center justify-center overflow-hidden rounded-full bg-[#0D1115]">
            {row.original.assetImage ? (
              <img
                src={row.original.assetImage}
                alt={row.original.assetLabel}
                className="h-full w-full object-contain"
              />
            ) : (
              <span className="text-[9px] font-bold text-[#869AA9]">
                {row.original.assetLabel.slice(0, 3)}
              </span>
            )}
          </div>
          <span className="truncate text-[12px] font-semibold text-white sm:text-[13px]">
            {row.original.assetLabel}
          </span>
        </div>
      ),
    },
    {
      id: "amount",
      accessorKey: "sortAmount",
      header: () => (
        <span className="text-[12px] font-medium text-[#869AA9]">Amount</span>
      ),
      cell: ({ row }) => (
        <span className="whitespace-nowrap text-[12px] font-medium text-[#BEE1FB] sm:text-[13px]">
          {row.original.amountText}
        </span>
      ),
    },
    {
      accessorKey: "balanceBeforeText",
      header: () => (
        <span className="text-[12px] font-medium text-[#869AA9]">
          Balance before
        </span>
      ),
      enableSorting: false,
      cell: ({ row }) => (
        <span
          className="line-clamp-2 text-[11px] text-[#869AA9] sm:text-[12px]"
          title={row.original.balanceBeforeText}
        >
          {row.original.balanceBeforeText}
        </span>
      ),
    },
    {
      accessorKey: "balanceAfterText",
      header: () => (
        <span className="text-[12px] font-medium text-[#869AA9]">
          Balance after
        </span>
      ),
      enableSorting: false,
      cell: ({ row }) => (
        <span
          className="line-clamp-2 text-[11px] text-[#869AA9] sm:text-[12px]"
          title={row.original.balanceAfterText}
        >
          {row.original.balanceAfterText}
        </span>
      ),
    },
    {
      accessorKey: "statusLabel",
      header: () => (
        <span className="text-[12px] font-medium text-[#869AA9]">Status</span>
      ),
      enableSorting: false,
      cell: ({ row }) => (
        <span className={statusBadgeClass(row.original.statusTone)}>
          {row.original.statusLabel}
        </span>
      ),
    },
    {
      id: "actions",
      header: () => (
        <span className="text-[12px] font-medium text-[#869AA9]"> </span>
      ),
      enableSorting: false,
      cell: ({ row }) => (
        <button
          type="button"
          onClick={(e) => {
            e.stopPropagation();
            onView(row.original.raw);
          }}
          className="rounded-lg border border-[#0B75C2]/40 bg-[#0B75C2]/10 px-3 py-1.5 text-[11px] font-semibold text-secondary_button transition-colors hover:bg-[#0B75C2]/20"
        >
          View
        </button>
      ),
    },
  ];
}

export type UserActivityTransactionsTableProps = {
  transactions: UserActivityTransaction[];
  isLoading: boolean;
  page: number;
  totalPages: number;
  transactionCount: number;
  statusFilter?: UserTransactionStatusFilter;
  onStatusFilterChange?: (status: UserTransactionStatusFilter) => void;
  onPageChange: (page: number) => void;
  onRowClick: (tx: UserActivityTransaction) => void;
  emptyImage: string;
};

export function UserActivityTransactionsTable({
  transactions,
  isLoading,
  page,
  totalPages,
  transactionCount,
  statusFilter = "",
  onStatusFilterChange,
  onPageChange,
  onRowClick,
  emptyImage,
}: UserActivityTransactionsTableProps) {
  const [sorting, setSorting] = useState<SortingState>([
    { id: "date", desc: true },
  ]);
  const tableSectionRef = useRef<HTMLDivElement>(null);
  const tableScrollRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    tableScrollRef.current?.scrollTo({ top: 0 });
    tableSectionRef.current?.scrollIntoView({ block: "start" });
  }, [page]);

  const data = useMemo(() => buildRows(transactions), [transactions]);
  const columns = useMemo(() => createColumns(onRowClick), [onRowClick]);

  const table = useReactTable({
    data,
    columns,
    state: { sorting },
    onSortingChange: setSorting,
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
    getRowId: (row) => String(row.id),
  });

  if (isLoading && transactions.length === 0) {
    return (
      <div className="mt-4 flex h-[400px] items-center justify-center">
        <TailSpin color="#0B75C2" height={50} width={70} />
      </div>
    );
  }

  if (!isLoading && transactions.length === 0) {
    return (
      <div className="mt-4 w-full">
        {onStatusFilterChange ? (
          <div className="mb-10 flex w-full max-w-[200px] flex-col gap-2 px-1">
            <h4 className="text-[10px] font-bold text-[#E7EBEE]">Status</h4>
            <div className="relative w-full">
              <select
                value={statusFilter}
                onChange={(e) =>
                  onStatusFilterChange(
                    e.target.value as UserTransactionStatusFilter
                  )
                }
                className="w-full appearance-none rounded-xl border border-[#2C363E] bg-[#161E25] px-4 py-2.5 pr-9 text-[12px] font-semibold text-[#E7EBEE] focus:border-[#4FB0FF] focus:outline-none focus:ring-2 focus:ring-[#4FB0FF]/20"
              >
                <option value="">All</option>
                <option value="COMPLETED">Completed</option>
                <option value="PENDING">Pending</option>
                <option value="FAILED">Failed</option>
              </select>
              <RiArrowDownSLine className="pointer-events-none absolute right-3 top-1/2 -translate-y-1/2 text-[18px] text-[#869AA9]" />
            </div>
          </div>
        ) : null}
        <div className="mb-60 flex w-full flex-col items-center justify-center">
          <img src={emptyImage} alt="" />
          <h4 className="pt-2 text-[14px] font-bold text-[#869AA9]">
            {statusFilter ? "No matching transactions" : "No transactions yet"}
          </h4>
          <p className="pt-2 text-[12px] text-[#869AA9]">
            {statusFilter
              ? "Try a different status filter"
              : "User transactions will show up here"}
          </p>
        </div>
      </div>
    );
  }

  return (
    <div ref={tableSectionRef} className="mt-8 w-full scroll-mt-6">
      <div className="mb-4 flex flex-wrap items-center justify-between gap-3 px-1 pt-1">
        <p className="text-[12px] text-[#617889]">
          {transactionCount} transaction{transactionCount === 1 ? "" : "s"}
        </p>
        {onStatusFilterChange ? (
          <div className="flex items-center gap-2">
            <span className="text-[11px] font-semibold text-[#869AA9]">
              Status
            </span>
            <div className="relative min-w-[140px]">
              <select
                value={statusFilter}
                onChange={(e) =>
                  onStatusFilterChange(
                    e.target.value as UserTransactionStatusFilter
                  )
                }
                className="w-full appearance-none rounded-lg border border-[#2C363E] bg-[#161E25] px-3 py-2 pr-8 text-[12px] font-semibold text-[#E7EBEE] focus:border-[#4FB0FF] focus:outline-none focus:ring-2 focus:ring-[#4FB0FF]/20"
              >
                <option value="">All</option>
                <option value="COMPLETED">Completed</option>
                <option value="PENDING">Pending</option>
                <option value="FAILED">Failed</option>
              </select>
              <RiArrowDownSLine className="pointer-events-none absolute right-2.5 top-1/2 -translate-y-1/2 text-[16px] text-[#869AA9]" />
            </div>
          </div>
        ) : null}
      </div>
      <div className="overflow-hidden rounded-xl border border-[#1C2227] bg-[#192025]">
        <div
          ref={tableScrollRef}
          className="max-h-[580px] overflow-auto scrollbar-thin scrollbar-track-[#192025] scrollbar-thumb-[#3A4852]"
        >
          <table className="w-full min-w-[1180px] border-collapse">
            <thead className="sticky top-0 z-10 bg-[#1C2227]">
              {table.getHeaderGroups().map((hg) => (
                <tr key={hg.id} className="border-b border-[#2C363E]">
                  {hg.headers.map((header) => (
                    <th
                      key={header.id}
                      className="px-4 py-3 text-left first:pl-5 last:pr-5"
                    >
                      {header.isPlaceholder
                        ? null
                        : flexRender(
                            header.column.columnDef.header,
                            header.getContext()
                          )}
                    </th>
                  ))}
                </tr>
              ))}
            </thead>
            <tbody>
              {table.getRowModel().rows.map((row) => (
                <tr
                  key={row.id}
                  onClick={() => onRowClick(row.original.raw)}
                  className="cursor-pointer border-b border-[#2C363E]/50 transition-colors last:border-b-0 hover:bg-[#1C2227]/60"
                >
                  {row.getVisibleCells().map((cell) => (
                    <td
                      key={cell.id}
                      className="px-4 py-3.5 align-middle first:pl-5 last:pr-5"
                    >
                      {flexRender(
                        cell.column.columnDef.cell,
                        cell.getContext()
                      )}
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {transactions.length >= 1 ? (
        <div className="mt-6 flex flex-wrap items-center justify-end gap-4 pb-16">
          <span className="text-[12px] text-[#869AA9]">
            {transactionCount} total · {USER_ACTIVITY_TX_PAGE_SIZE} per page
          </span>
          <span className="font-sora text-[14px] font-bold text-white">
            Page {page} of {Math.max(1, totalPages)}
          </span>
          <div className="flex items-center gap-2">
            <button
              type="button"
              disabled={page <= 1}
              onClick={() => page > 1 && onPageChange(page - 1)}
              className="disabled:opacity-40"
            >
              <BsArrowLeftSquare className="cursor-pointer text-3xl text-[#C6D0D7]" />
            </button>
            <button
              type="button"
              disabled={page >= totalPages}
              onClick={() => page < totalPages && onPageChange(page + 1)}
              className="disabled:opacity-40"
            >
              <BsArrowRightSquare className="cursor-pointer text-3xl text-[#C6D0D7]" />
            </button>
          </div>
        </div>
      ) : null}
    </div>
  );
}
